export interface ChoiceOption {
  label: string;
  value: string;
}

export interface Step {
  id: string;
  type: "single" | "multi" | "phq9" | "contact" | "intro" | "result";
  question: string;
  helpText?: string;
  options?: ChoiceOption[];
  showIf?: (a: Record<string, string | string[]>) => boolean;
}

export type Answers = Record<string, string | string[]>;

// ── Question flow (no safety screen) ──
export const STEPS: Step[] = [
  { id: "intro", type: "intro", question: "" },
  {
    id: "age",
    type: "single",
    question: "What is your age range?",
    options: [
      { label: "Under 18", value: "under18" },
      { label: "18–21", value: "18-21" },
      { label: "22–80", value: "22-80" },
      { label: "Over 80", value: "over80" },
    ],
  },
  {
    id: "depression_dx",
    type: "single",
    question:
      "Have you been diagnosed with depression by a doctor or mental health professional?",
    options: [
      { label: "Yes — Major Depressive Disorder", value: "mdd" },
      { label: "Yes — Treatment-Resistant Depression", value: "trd" },
      { label: "Yes — other or not sure of the name", value: "other" },
      { label: "No", value: "no" },
    ],
  },
  {
    id: "current_episode",
    type: "single",
    question: "Are you currently experiencing a depressive episode?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
  },
  {
    id: "meds_count",
    type: "single",
    question: "How many antidepressant medications have you tried?",
    options: [
      { label: "0", value: "0" },
      { label: "1", value: "1" },
      { label: "2–3", value: "2-3" },
      { label: "4 or more", value: "4+" },
    ],
  },
  {
    id: "meds_30days",
    type: "single",
    question: "Did you take each of those medications for at least 30 days?",
    showIf: (a) => {
      const v = a["meds_count"];
      return !!v && v !== "0";
    },
    options: [
      { label: "Yes, all of them", value: "all" },
      { label: "Some of them", value: "some" },
      { label: "No", value: "no" },
      { label: "Not sure", value: "notsure" },
    ],
  },
  {
    id: "meds_stopped_reason",
    type: "multi",
    question: "Why did you stop? (Select all that apply)",
    showIf: (a) => {
      const v = a["meds_count"];
      return !!v && v !== "0";
    },
    options: [
      { label: "Didn't help enough", value: "nohelp" },
      { label: "Side effects", value: "sideeffects" },
      { label: "Still taking them", value: "still" },
      { label: "Other", value: "other" },
    ],
  },
  {
    id: "psychiatrist",
    type: "single",
    question: "Have you been evaluated by a psychiatrist?",
    options: [
      { label: "Yes, currently under care", value: "current" },
      { label: "Yes, in the past", value: "past" },
      { label: "No, only a primary care doctor", value: "pcp" },
      { label: "No", value: "no" },
    ],
  },
  {
    id: "talk_therapy",
    type: "single",
    question: "Have you tried talk therapy for depression?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
  },
  {
    id: "other_treatments",
    type: "multi",
    question: "Have you tried any of these? (Select all that apply)",
    options: [
      { label: "Standard TMS", value: "tms" },
      { label: "Ketamine or Spravato", value: "ketamine" },
      { label: "ECT", value: "ect" },
      { label: "None", value: "none" },
    ],
  },
  {
    id: "phq9",
    type: "phq9",
    question:
      "Over the last 2 weeks, how often have you been bothered by these problems?",
  },
  {
    id: "result",
    type: "result",
    question:
      "Based on your answers, here's what we'd recommend discussing with a clinician.",
  },
  {
    id: "contact",
    type: "contact",
    question: "Want our clinical team to reach out? (Optional)",
    helpText:
      "Optional — only share your contact info if you'd like our team to contact you. Your on-screen result above is complete either way.",
  },
];

export function getVisibleSteps(answers: Answers): Step[] {
  return STEPS.filter((s) => {
    if (!s.showIf) return true;
    return s.showIf(answers);
  });
}
